'use client'

import { useEffect, useRef, useState } from 'react'

import { Button, OverlayArrow, Tooltip, TooltipTrigger } from 'react-aria-components'

const apiKey = process.env.NEXT_PUBLIC_SEJDA_API_KEY || null

export default function DownloadBtn({
  filename,
  btnClass = 'inline-flex h-40 w-40 items-center justify-center rounded-sm text-fg-title hover:bg-theme-accent transition-colors duration-200 ease-in-out',
}: {
  filename?: string
  btnClass?: string
}) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [downloadName, setFilename] = useState(filename ?? null)
  const btnRef = useRef(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setFilename(document.title)
    }
  }, [])

  function handleDownload(filename = null, ref = null) {
    if (!apiKey) {
      console.error('Sejda API key is missing')
      return
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.error('This function should be called in the browser')
      return
    }

    setIsProcessing(true)

    filename = filename ?? document.title

    // We have to use url rather than `htmlCode` because Next uses relative urls for assets
    fetch('https://api.sejda.com/v2/html-pdf', {
      cache: 'no-cache',
      method: 'POST',
      headers: {
        'Authorization': 'Token: ' + apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: filename + '.pdf',
        pageOrientation: 'portrait',
        pageSize: 'a4',
        pageMargin: 0,
        usePrintMedia: false,
        url: window.location.href,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Got code: ' + response.status)
        }
        return response.blob()
      })
      .then((blob) => {
        // Create a download link, click it, and remove it
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename + '.pdf'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        setIsProcessing(false)
      })
      .catch((err) => {
        setIsProcessing(false)
        console.error(err)
      })
  }

  if (!apiKey) {
    return null
  }

  btnClass += isProcessing ? ' animate-pulse bg-primary-200 ' : ''

  return (
    <TooltipTrigger delay={0}>
      <Button className={btnClass} onPress={() => handleDownload(downloadName)} ref={btnRef}>
        <svg className="pointer-events-none h-24 w-24" aria-hidden="true">
          <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-download`}></use>
        </svg>
      </Button>
      <Tooltip>
        <OverlayArrow>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>

        {isProcessing ? 'Generating...' : 'Download'}
      </Tooltip>
    </TooltipTrigger>
  )
}
